import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Shield, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import ResponsiveTable from "../components/ResponsiveTable";
import { getTenants } from "../apiHelper";

const Accounts = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const { getAccessTokenSilently } = useAuth();

  useEffect(() => {
    setLoading(true);
    getAccessTokenSilently().then((t) => {
      getTenants(t, { isTrial: false, keyword: search }).then((tenants) => {
        setTenants(tenants.data);
        setLoading(false);
      });
    });
  }, [search]);

  // Debounce effect for search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(inputValue);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue]);

  const columns = [
    {
      key: "accountName",
      label: "Account",
      mobileLabel: "Account",
      render: (tenant) => (
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-purple-100">
            <Shield className="h-4 w-4 text-purple-800" />
          </div>
          <div className="font-medium text-gray-900">{tenant.accountName}</div>
        </div>
      ),
    },
    {
      key: "name",
      label: "Database Name",
      mobileLabel: "Database",
      render: (tenant) => (
        <div className="text-sm text-gray-600 font-mono">{tenant.name}</div>
      ),
    },
    {
      key: "subscriptionPlan",
      label: "Subscription Plan",
      mobileLabel: "Plan",
      render: (tenant) => (
        <Badge variant="secondary" className="bg-blue-100 text-blue-800">
          {tenant.subscriptionPlan || "basic"}
        </Badge>
      ),
    },
    {
      key: "userLicenses",
      label: "User Licenses",
      mobileLabel: "Licenses",
      render: (tenant) => {
        const currentUsers = tenant.currentUserCount || 0;
        const licenseCount = tenant.userLicenseCount;
        const isTrial = tenant.accountType === "trial" || !licenseCount;

        if (isTrial) {
          return (
            <div className="text-sm font-semibold text-blue-600">
              {currentUsers}/Unlimited
            </div>
          );
        }

        const isOverLimit = currentUsers >= licenseCount;
        return (
          <div
            className={`text-sm font-semibold ${
              isOverLimit ? "text-red-600" : "text-green-600"
            }`}
          >
            {currentUsers}/{licenseCount}
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      mobileLabel: "Status",
      render: () => (
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          Active
        </Badge>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      mobileLabel: "Created",
      render: (tenant) => (
        <div className="text-sm text-gray-600">
          {new Date(tenant.createdAt).toLocaleDateString()}
        </div>
      ),
    },
  ];

  const handleRowClick = (tenant) => {
    window.location.href = `/${tenant._id}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-3 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8 w-full overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Accounts
            </h1>
            <p className="text-sm lg:text-base text-gray-600">
              Manage and track your accounts
            </p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="space-y-4 lg:space-y-6">
          <div className="flex flex-col sm:flex-row gap-4 lg:gap-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 lg:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 lg:h-5 lg:w-5" />
                <Input
                  placeholder="Search accounts..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="pl-10 lg:pl-12 h-10 lg:h-12 rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200 shadow-sm text-sm lg:text-base"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Accounts Table */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl shadow-gray-200/50 rounded-2xl">
          <CardHeader className="pb-4 lg:pb-6">
            <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                <Shield className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
              </div>
              <span className="text-base lg:text-xl">Account List</span>
              {tenants && (
                <Badge
                  variant="outline"
                  className="ml-2 lg:ml-3 bg-purple-50 text-purple-700 border-purple-200 px-2 py-1 lg:px-3 lg:py-1 rounded-lg text-xs lg:text-sm"
                >
                  {tenants.length} accounts
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ResponsiveTable
              columns={columns}
              data={tenants}
              onRowClick={handleRowClick}
              loading={loading}
              emptyMessage="No accounts match your search criteria."
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Accounts;
